import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Card, CardBody, Row, Col, Button, Spinner, Table, Progress } from "reactstrap";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { getBulkScheduleSummary } from "../../../../helpers/api_rombo_helper";
import TaskStatusBadge from "../components/TaskStatusBadge";
import BreadcrumbLink from "../../../../components/Common/BreadcrumbLink";
import i18n from "../../../../i18n";

const selectToken = createSelector((state) => state.Login, (l) => l.token);

const BulkTaskDetail = () => {
  const { id } = useParams();
  const token = useSelector(selectToken);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  document.title = `Detalle de Tarea - Programación masiva`;

  useEffect(() => {
    if (!token || !id) return;

    getBulkScheduleSummary(token, "list", "json")
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items ?? [];
        const selectedTask = items.find(task => task.document_id === id);
        setTask(selectedTask || null);
      })
      .catch(() => {
        setTask(null);
      })
      .finally(() => setLoading(false));
  }, [token, id]);

  const handleDownload = async () => {
    if (!task?.file_summary) return;

    setDownloading(true);
    try {
      const publicUrl = task.file_summary.replace(
        "s3://requests.services.stg/",
        "https://requests.services.stg.s3.amazonaws.com/"
      );

      window.open(publicUrl,"_blank");
    } catch (_) {
      console.error("Error downloading file:", _);
    } finally {
      setDownloading(false);
    }
  };

  const formatLongDate = (value) => {
    if (!value) return "—";

    const [datePart,timePart] = value.split(" ");
    const [day,month,year] = datePart.split("-");

    const months = [
      "enero","febrero","marzo","abril","mayo","junio",
      "julio","agosto","septiembre","octubre","noviembre","diciembre"
    ];

    let [hours,minutes,seconds] = timePart.split(":");
    let suffix = "a.m.";

    hours = Number(hours);

    if (hours >= 12) suffix = "p.m.";
    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;

    return `${Number(day)} de ${months[Number(month)-1]} del ${year} - ${hours}:${minutes}:${seconds} ${suffix}`;
  };

  const rows = task?.services ?? task?.items ?? [];
  const completed = task?.completed ?? 0;
  const total = task?.total_services ?? task?.total ?? rows.length;
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="page-content">
      <Row className="mb-3">
          <Col>
            <BreadcrumbLink
              title={i18n.t("MassiveServiceLoad")}
              link="/services/bulk-schedule/tasks"
              breadcrumbItem={i18n.t("MassiveUploadRequest")}
            />
          </Col>
        </Row>
      <Container fluid>
        <Row className="mb-3">
          <Col/>
        </Row>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : !task ? (
          <Card>
            <CardBody>
              <p className="text-muted text-center py-4">
                Tarea no encontrada
              </p>
            </CardBody>
          </Card>
        ) : (
          <>
            <Card className="mb-3">
              <CardBody>
                <Col className="text-end">
                  <Button className="btn btn-request" outline size="sm" onClick={handleDownload} disabled={downloading}>
                    Descargar resumen Excel
                  </Button>
                </Col>
                <Row className="g-3">
                  <Col md={3}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Empresa</div>
                    <strong>{task.company_name ?? task.partner_name ?? task.company ?? "—"}</strong>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Estado</div>
                    <TaskStatusBadge status={task.status} />
                  </Col>
                  <Col md={6}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Fecha y Hora</div>
                    <span style={{ fontSize: 13 }}>
                      {formatLongDate(task.created_at)}
                    </span>
                  </Col>
                </Row>
                <Row className="mt-3 g-3">
                  <Col md={3}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Folio</div>
                    <strong>{task.document_id ?? "—"}</strong>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Vehículos solicitados</div>
                    <strong>{task.total_vehicles ?? "—"}</strong>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Pasajeros totales</div>
                    <strong>{task.total_rows ?? "—"}</strong>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            {rows.length > 0 && (
              <Card>
                <CardBody>
                  <h6 className="mb-3">Detalle de servicios</h6>
                  <div style={{ maxHeight: 400, overflowY: "auto" }}>
                    <Table bordered responsive size="sm">
                      <thead className="table-light">
                        <tr>
                          {Object.keys(rows[0]).map((k) => <th key={k}>{k}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, i) => (
                          <tr key={i}>
                            {Object.values(row).map((v, j) => <td key={j}>{String(v ?? "")}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default BulkTaskDetail;
